import React from 'react';
import { ArrowRight, Cpu, Gauge, GitBranch, Layers, Search, Server, Zap } from 'lucide-react';
import { addonBySlug } from './addonsData';
import { CodeBlock } from '../CodeBlock';

const gobed = addonBySlug['gobed'];

const benchRows = [
  ['1K docs', 'flat (exact)', '357 µs', '~2,800 QPS'],
  ['100K docs', 'flat (exact)', '2.23 ms', 'NDCG@10 > 0.99'],
  ['100K docs', 'IVF', '417 µs', '~2,400 QPS'],
  ['500K docs', 'IVF', '~1.4 ms', '~715 QPS'],
  ['1M docs', 'GPU batch', '—', '~1,050 QPS sustained'],
];

const apiRows = [
  ['POST /search', 'single or multi query, k, timeout_ms → results with similarity scores'],
  ['POST /batch_search', 'high-throughput query batches'],
  ['POST /index', 'index documents [{id, text}], sync or async'],
  ['POST /batch_index', 'bulk ingestion'],
  ['GET /health · /metrics · /gpu_stats', 'liveness, Prometheus metrics, GPU utilization'],
];

const indexModes = [
  {
    icon: Zap,
    name: 'Flat',
    detail: 'Exact brute-force with SIMD (AVX2/arm64) dot products. Default under ~1.5K vectors, zero recall loss.',
  },
  {
    icon: Layers,
    name: 'IVF + HNSW routing',
    detail: 'K-means inverted lists with an HNSW graph over centroids. ~3× faster at scale for ~10% recall trade, tunable via nprobe.',
  },
  {
    icon: Cpu,
    name: 'CAGRA on GPU',
    detail: 'NVIDIA cuVS CAGRA graph index with custom fused CUDA kernels, GPU k-means, and a pooled memory manager. Auto-enabled when CUDA is present.',
  },
];

export const GobedAddonPage: React.FC = () => (
  <main className="overflow-hidden bg-black text-white">
    <section className="relative border-b border-white/10 px-4 pb-14 pt-24 sm:px-8 lg:px-12">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(56,189,248,0.12),transparent_55%)]" />
      <div className="relative mx-auto max-w-5xl">
        <div className="inline-flex items-center gap-2 rounded-full border border-sky-400/25 bg-sky-400/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-sky-300">
          <Search size={12} /> First-party addon · flagship
        </div>
        <h1 className="mt-6 max-w-[18ch] font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-6xl">
          gobed: GPU vector search as a service
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-8 text-white/72">
          The search engine we built for app.nz, as an addon: a 15 MB int8 embedding model, exact and
          IVF/HNSW indexes on CPU, cuVS CAGRA graph search on GPU, three HTTP endpoints, one injected env
          var. It powers character, art, audio, and repo search here.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="/signup" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-sky-400 px-6 text-sm font-bold text-black transition hover:bg-sky-300">
            Attach gobed <ArrowRight size={16} />
          </a>
          <a href="/addons" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-white/12 bg-white/[0.04] px-6 text-sm font-bold text-white transition hover:bg-white hover:text-black">
            All addons
          </a>
        </div>
      </div>
    </section>

    <section className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
      <div className="mx-auto max-w-5xl">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-sky-400/90">why it is fast</p>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">Static embeddings, one index per scale</h2>
        <p className="mt-5 max-w-3xl text-base leading-8 text-white/72">
          gobed embeds with static retrieval embeddings (token vectors + mean pooling, no attention pass),
          quantized to int8 at 512 dimensions: a 15 MB model embedding ~150K texts/sec on CPU and using
          roughly 600 bytes per stored vector, so a million documents fit in ~600 MB. The index is picked per
          corpus:
        </p>
        <div className="mt-8 grid gap-4 lg:grid-cols-3">
          {indexModes.map((m) => (
            <div key={m.name} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
              <m.icon size={20} className="text-sky-400" />
              <h3 className="mt-4 font-display text-lg font-bold">{m.name}</h3>
              <p className="mt-2 text-sm leading-7 text-white/65">{m.detail}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 overflow-x-auto rounded-2xl border border-white/10">
          <table className="w-full min-w-[560px] text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-white/[0.04]">
                {['Corpus', 'Index', 'Latency', 'Throughput / quality'].map((h) => (
                  <th key={h} className="px-4 py-3 text-xs font-bold uppercase tracking-[0.14em] text-white/55">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {benchRows.map((r) => (
                <tr key={r[0] + r[1]} className="border-b border-white/5 last:border-0">
                  {r.map((c, j) => (
                    <td key={j} className={`px-4 py-3 leading-6 ${j === 0 ? 'font-semibold text-white' : 'text-white/68'}`}>{c}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-sm leading-7 text-white/55">
          Measured on our benchmark suite (RTX 3090 for GPU rows); methodology ships in the repo.
          Sub-millisecond CAGRA at 100K+ docs is the current target, not a shipped measurement.
        </p>
      </div>
    </section>

    <section className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
      <div className="mx-auto max-w-5xl">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-sky-400/90">the addon</p>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">Three endpoints, one env var</h2>
        <div className="mt-8 grid gap-4 lg:grid-cols-2">
          <div className="overflow-x-auto rounded-2xl border border-white/10">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-white/10 bg-white/[0.04]">
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-[0.14em] text-white/55">Endpoint</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-[0.14em] text-white/55">Purpose</th>
                </tr>
              </thead>
              <tbody>
                {apiRows.map((r) => (
                  <tr key={r[0]} className="border-b border-white/5 last:border-0">
                    <td className="px-4 py-3 font-mono text-[12px] font-semibold text-white">{r[0]}</td>
                    <td className="px-4 py-3 leading-6 text-white/68">{r[1]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/45">Injected environment</p>
            <div className="mt-4 space-y-3">
              {gobed.envVars.map((v) => (
                <div key={v.name}>
                  <code className="font-mono text-[13px] font-semibold text-sky-300">{v.name}</code>
                  <p className="text-sm leading-6 text-white/60">{v.description}{v.secret ? ' · secret' : ''}</p>
                </div>
              ))}
            </div>
            <CodeBlock
              language="bash"
              preClassName="!text-[13px] !leading-6"
              code={`curl "$GOBED_SEARCH_URL/search" \\
  -H "Authorization: Bearer $GOBED_API_KEY" \\
  -d '{"query": "cozy sci-fi reading nook", "k": 5}'`}
            />
          </div>
        </div>
        <div className="mt-8 grid gap-3 sm:grid-cols-3">
          {(gobed.plans || []).map((p) => (
            <div key={p.plan} className="rounded-xl border border-white/10 bg-white/[0.04] p-5">
              <p className="font-display text-lg font-bold">{p.plan}</p>
              <p className="mt-1 text-sm font-semibold text-sky-400">{p.credits}</p>
              <p className="mt-2 text-sm leading-6 text-white/60">{p.detail}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
      <div className="mx-auto max-w-5xl">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-sky-400/90">dogfood</p>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">It runs this site</h2>
        <p className="mt-5 max-w-3xl text-base leading-8 text-white/72">
          The in-process int8 engine indexes app.nz characters, art, audio, documents, and repo code. The
          GPU sidecar serves <code className="mx-1 font-mono text-sm">/api/search-ais</code> semantic
          character search, with a SQLite fallback when absent. The addon gives you that: GPU fast path,
          SQLite fallback, one env var.
        </p>
        <div className="mt-6 flex flex-wrap gap-3 text-sm">
          {[
            { label: 'character search', href: '/characters' },
            { label: 'art search', href: '/art' },
            { label: 'repo code search', href: '/repos' },
            { label: 'embeddings API demo', href: '/tools/text-embeddings' },
            { label: 'semantic search demo', href: '/tools/semantic-search' },
            { label: 'GoBrain memory', href: 'https://gpubrain.app.nz' },
          ].map((l) => (
            <a key={l.label} href={l.href} className="rounded-full border border-white/12 bg-white/[0.04] px-4 py-1.5 font-semibold text-white/70 transition hover:border-white/25 hover:text-white">
              {l.label}
            </a>
          ))}
        </div>
      </div>
    </section>

    <section className="px-4 py-14 sm:px-8 sm:py-20 lg:px-12">
      <div className="relative mx-auto max-w-5xl overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] px-6 py-12 text-center sm:px-12">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(56,189,248,0.14),transparent_60%)]" />
        <div className="relative">
          <h2 className="font-display text-2xl font-bold tracking-tight sm:text-4xl">Metered per index, no platform fee</h2>
          <p className="mx-auto mt-4 max-w-2xl text-[15px] leading-8 text-white/70">
            No per-seat pricing, no minimum clusters: a metered index on shared CPU, or a per-second GPU pod
            for CAGRA.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a href="/signup" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-sky-400 px-6 text-sm font-bold text-black transition hover:bg-sky-300">
              Get started
            </a>
            <a href="/addons/postgres" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-white/12 bg-white/[0.04] px-6 text-sm font-bold text-white transition hover:bg-white hover:text-black">
              Pair it with Postgres <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </div>
    </section>
  </main>
);

export default GobedAddonPage;
